import dayjs from "dayjs";
import * as Yup from "yup";
import { ACCOUNT_NO_REGEX, EMAIL_REGEX, MOBILE_REGEX } from "./Regexes";
import { RESTRICTED_EMAIL_DOMAINS } from "./Texts";
export const phoneValidation = Yup.string()
  .required("Phone Number is Required")
  .length(10, "Mobile number must be exactly 10 digits long")
  .matches(
    /^[6-9]\d{9}$/,
    "Please enter a valid mobile number starting with 6, 7, 8, or 9",
  )
  .notOneOf(
    ["6666666666", "7777777777", "8888888888", "9999999999"],
    "This phone number is not allowed",
  );
export const phoneDevValidation = Yup.string()
  .required("Phone Number is Required")
  .length(10, "Mobile number must be exactly 10 digits long")
  .notOneOf(
    ["6666666666", "7777777777", "8888888888", "9999999999"],
    "This phone number is not allowed",
  );
const dateOfBirthvalidation = Yup.date()
  .required("DOB is required")
  .test(
    "DOB",
    "You must be at least 18 years old",
    (value) => dayjs().diff(dayjs(value), "year") >= 18,
  );
const panValidation = Yup.string()
  .matches(
    "^([a-zA-Z]){5}([0-9]){4}([a-zA-Z]){1}?$",
    "Please enter a valid PAN number",
  )
  .required("PAN is Required")
  .max(10, "PAN should be 10 digits")
  .min(10, "PAN should be 10 digits")
  .test("is-individual-pan", "Only Individual PAN are allowed", (value) => {
    return !value || value[3] === "P" || value[3] === "p";
  });
const bankValidation = Yup.string().required("Bank is Required");
const accountNumberValidation = Yup.string()
  .required("Please enter your account Number")
  .matches(ACCOUNT_NO_REGEX, "Your account number is invalid");
const confirmAccountNumberValidation = Yup.string()
  .oneOf(
    [Yup.ref("accountNumber"), null],
    "Entered account number does not match",
  )
  .required("Please confirm account number");
const ifscCodeValidation = Yup.string()
  .required("IFSC Code is Required")
  .matches(/^[A-Za-z]{4}0[A-Za-z0-9]{6}$/, "Invalid IFSC code");
export const upiIDValidation = Yup.string()
  .required("Upi ID is Required")
  .matches(
    "^[a-zA-Z0-9._-]{2,256}@[a-zA-Z][a-zA-Z0-9]{1,63}$",
    "Invalid UPI ID",
  );

export const LOGIN_SCHEMA = Yup.object().shape({
  phone:
    process.env.NEXT_PUBLIC_ENVIRONMENT === "production"
      ? phoneValidation
      : phoneDevValidation,
  terms: Yup.boolean().oneOf(
    [true],
    "Accepting Terms and Conditions is required",
  ),
  whatsapp: Yup.boolean(),
});
export const BANK_SCHEMA = Yup.object().shape({
  bankName: bankValidation,
  accountNumber: accountNumberValidation,
  confirmAccountNumber: confirmAccountNumberValidation,
  bankIFSCCode: ifscCodeValidation,
});

const normalizeAccountNumber = (value = "") =>
  String(value).replace(/\s+/g, "");

export const getBankSchemaAAPerfios = (bankAccountNumber) =>
  Yup.object().shape({
    bankName: bankValidation,

    accountNumber: Yup.string()
      .required("Please enter your account Number")
      .matches(ACCOUNT_NO_REGEX, "Your account number is invalid")
      .test(
        "last-four-match",
        "The account number entered by you differs from the account we validated.",
        function (value) {
          if (!value || !bankAccountNumber) return true;

          const enteredAccount = normalizeAccountNumber(value);
          const bankLast4 = String(bankAccountNumber)
            .replace(/\D/g, "")
            .slice(-4);

          const enteredLast4 = enteredAccount.slice(-4);

          if (bankLast4.length !== 4) return true;

          return enteredLast4 === bankLast4;
        },
      ),

    confirmAccountNumber: Yup.string()
      .required("Please confirm your account number")
      .oneOf(
        [Yup.ref("accountNumber")],
        "Entered account number does not match",
      ),

    bankIFSCCode: ifscCodeValidation,
  });

export const PAN_SCHEMA = Yup.object().shape({
  pan: panValidation,
  consent: Yup.boolean()
    .oneOf([true], "Consent is Required")
    .required("Consent is Required"),
  panType: Yup.string()
    .required("PAN Type is Required")
    .oneOf(
      ["individual"],
      "We only offer personal loans to individuals. Please provide your Individual PAN to proceed.",
    ),
});
export const CKYC_SCHEMA = Yup.object().shape({
  firstName: Yup.string(),
  lastName: Yup.string(),
  pan: panValidation,
  dob: Yup.string().required("DOB is required"),
});
export const ADDRESS_SCHEMA = Yup.object().shape({
  country: Yup.string().oneOf(["India"], "Address country must be India"),
  pin: Yup.string().required("Pincode is required"),
  line1: Yup.string()
    .required("House No/Flat No/Plot No/Floor No/Tower is required")
    .min(10, "Min 10 characters are required"),
  line2: Yup.string()
    .required("Apartment/Street/Building & Locality/Area is required")
    .min(10, "Min 10 characters are required"),
  line4: Yup.string()
    .notRequired()
    .nullable()
    .transform((value) => (value === "" ? null : value))
    .min(10, "Min 10 characters are required"),
  landmark: Yup.string()
    .required("Landmark is required")
    .min(10, "Min 10 characters are required"),
  state: Yup.string()
    .trim()
    .required("State is required. Please search a valid state name in English.")
    .matches(
      /^[A-Za-z\s.,'()-]+$/,
      "Please search a valid state name in English.",
    ),
  city: Yup.string()
    .trim()
    .required("City is required. Please search a valid city name in English.")
    .matches(
      /^[A-Za-z\s.,'()-]+$/,
      "Please enter a valid city name in English.",
    ),
});
export const PERSONAL_DETAILS_SCHEMA = Yup.object().shape({
  gender: Yup.string().required("Gender is Required"),
  fatherName: Yup.string().required("Father's Name is required"),
  motherName: Yup.string().required("Mother's Name is required"),
  employmentType: Yup.string().required("Employment type is required"),
  industryType: Yup.string().required("Industry Type is required"),
  companyName: Yup.string().when("employmentType", {
    is: (val) => val === "Salaried",
    then: () =>
      Yup.string()
        .required("Company Name is Required")
        .matches(/^[a-zA-Z0-9_. -()]+$/, "Please enter valid Company Name")
        .test(
          "no-multiple-spaces",
          "Company Name cannot contain multiple spaces",
          (value) => !/\s{2,}/.test(value || ""),
        )
        .test(
          "no-leading-trailing-spaces",
          "Company Name cannot start or end with spaces",
          (value) => (value || "").trim() === value,
        ),
    otherwise: () => Yup.string().notRequired(),
  }),
  businessName: Yup.string().when("employmentType", {
    is: (val) => val === "Self Employed",
    then: () =>
      Yup.string()
        .required("Business Name is Required")
        .matches(/^[a-zA-Z0-9_. -()]+$/, "Please enter valid Business Name")
        .test(
          "no-multiple-spaces",
          "Business Name cannot contain multiple spaces",
          (value) => !/\s{2,}/.test(value || ""),
        )
        .test(
          "no-leading-trailing-spaces",
          "Business Name cannot start or end with spaces",
          (value) => (value || "").trim() === value,
        ),
    otherwise: () => Yup.string().notRequired(),
  }),
  // netIncome: Yup.string().required("Net Income is required"),
});

const emailValidation = Yup.string()
  .required("Personal Email is Required")
  .matches(EMAIL_REGEX, "Invalid Email");
// .email("Invalid Personal Email");

export const WORK_DETAILS_SCHEMA = Yup.object().shape(
  {
    employmentType: Yup.string(),
    line1: Yup.string().when("employmentType", {
      is: (val) => val === "Self Employed",
      then: () =>
        Yup.string()
          .trim()
          .required("Business Name is required")
          .matches(/^[a-zA-Z0-9_. -()]+$/, "Please enter valid Business Name")
          .test(
            "no-multiple-spaces",
            "Business Name cannot contain multiple spaces",
            (value) => !/\s{2,}/.test(value || ""),
          )
          .test(
            "no-leading-trailing-spaces",
            "Business Name cannot start or end with spaces",
            (value) => (value || "").trim() === value,
          ),
      otherwise: () =>
        Yup.string()
          .trim()
          .required("Company Name is required")
          .matches(/^[a-zA-Z0-9_. -()]+$/, "Please enter valid Company Name")
          .test(
            "no-multiple-spaces",
            "Company Name cannot contain multiple spaces",
            (value) => !/\s{2,}/.test(value || ""),
          )
          .test(
            "no-leading-trailing-spaces",
            "Company Name cannot start or end with spaces",
            (value) => (value || "").trim() === value,
          ),
    }),
    line2: Yup.string()
      .trim()
      .required("Floor No/Tower No/Building No is required")
      .min(10, "Min 10 characters are required"),
    line3: Yup.string()
      .trim()
      .required("Street/Locality/Area is required")
      .min(10, "Min 10 characters are required"),
    line5: Yup.string()
      .trim()
      .notRequired()
      .nullable()
      .transform((value) => (value === "" ? null : value))
      .min(10, "Min 10 characters are required"),
    line4: Yup.string()
      .trim()
      .required("Address is required")
      .transform((value) => (value === "" ? null : value)),
    maritalStatus: Yup.string().required("Marital status is required"),
    aadharMobileNumber: Yup.string()
      .matches(MOBILE_REGEX, "Mobile number is not valid")
      .notRequired()
      .matches(/^[6-9]\d{9}$/, "Invalid Mobile Number"),
    education: Yup.string().required("Education is required"),
    purposeOfLoan: Yup.string().required("Purpose of Loan is required"),
    language: Yup.string().required("Language is required"),
    netAnnualIncome: Yup.number()
      .required("Net Annual Income is required")
      .integer()
      .min(180000, "Net Annual Income should be minimum 1,80,000")
      .integer()
      .max(10000000, "Net Annual Income should be less than 10000000"),
    emailVerified: Yup.boolean().oneOf([true], "Please Verify Email"),
    personalEmailId: emailValidation.when("workEmailId", {
      is: (val) => val !== "" && val !== undefined,
      then: (schema) =>
        schema.notOneOf(
          [Yup.ref("workEmailId")],
          "Personal Email cannot be the same as Work Email",
        ),
      otherwise: (schema) => schema,
    }),
    workEmailId: Yup.string()
      .trim()
      .matches(EMAIL_REGEX, "Invalid Email")
      .when("personalEmailId", {
        is: (val) => val !== "" && val !== undefined,
        then: () =>
          Yup.string()
            .notOneOf(
              [Yup.ref("personalEmailId")],
              "Work Email cannot be the same as Personal Email",
            )
            .min(10, "Min 10 characters are required")
            .test(
              "not-restricted-domain",
              "Please use your official work email.",
              (value) => {
                if (!value) return true;
                const domain = value.split("@")[1]?.toLowerCase();
                if (!domain) return true;
                return !RESTRICTED_EMAIL_DOMAINS.some(
                  (d) => d.toLowerCase() === domain,
                );
              },
            ),
        otherwise: () => Yup.string().notRequired(),
      }),
    landmark: Yup.string()
      .trim()
      .required("Landmark is required")
      .min(10, "Min 10 characters are required"),
    pin: Yup.string().trim(),
    state: Yup.string()
      .trim()
      .required(
        "State is required. Please search a valid state name in English.",
      )
      .matches(
        /^[A-Za-z\s.,&/'()-]+$/,
        "Please search a valid state name in English.",
      ),
    city: Yup.string()
      .trim()
      .required("City is required. Please search a valid city name in English.")
      .matches(
        /^[A-Za-z\s.,&/'()-]+$/,
        "Please enter a valid city name in English.",
      ),

    country: Yup.string().oneOf(["India"], "Address country must be India"),
    officialEmailVerified: Yup.boolean().when("workEmailId", {
      is: (val) => val !== "" && val !== undefined,
      then: () =>
        Yup.boolean()
          .oneOf([true], "Please verify your official email")
          .required("Email verification is required"),
      otherwise: () => Yup.boolean().notRequired(),
    }),
  },
  [["personalEmailId", "workEmailId"]],
);

export const NOMINEE_SCHEMA = Yup.object().shape({
  nomineeDob: dateOfBirthvalidation,
  nomineeMobileNumber: phoneValidation,
  nomineeName: Yup.string().required("Nominee Name is required"),
  nomineeRelation: Yup.string().required("Relationship is required"),
});

export const ACCOUNT_AGGREGATOR_SCHEMA = Yup.object().shape({
  accountNumber: accountNumberValidation,
  confirmAccountNumber: confirmAccountNumberValidation,
  consent: Yup.boolean()
    .oneOf([true], "You need to provide consent to be able to proceed")
    .required()
    .default(false),
});

export const DIGI_KYC_SCHEMA = Yup.object().shape({
  consent: Yup.boolean()
    .oneOf(
      [true],
      "I consent to Hero FinCorp Ltd. (HFCL) to collect, store, and verify my KYC details from CKYCR/DigiLocker, along with my selfie, through Hyperverge Technologies Private Limited.",
    )
    .required()
    .default(false),
});

export const CKYC_PHONE_SCHEMA = Yup.object().shape({
  phone:
    process.env.NEXT_PUBLIC_ENVIRONMENT === "production"
      ? phoneValidation
      : phoneDevValidation,
});
